import React from 'react';

const ProfilePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-ocean-100">
      <div className="section">
        <h1 className="section-header">Company Profile</h1>
        
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div className="maritime-card p-8">
            <h2 className="text-2xl font-bold text-ocean-800 mb-6">About PetroMarine</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              PetroMarine Offshore Projects Support Company is a leading provider of comprehensive 
              marine services to the offshore oil and gas industry in the Middle East. Established 
              with a vision to deliver quality services, we have built a reputation for excellence 
              in vessel management, crew services, and offshore project support.
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Our commitment to safety, environmental protection, and operational excellence has 
              made us a trusted partner for major oil and gas companies operating in the region.
            </p>
            
            {/* Company Image */}
            <div className="mt-6 rounded-xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                alt="PetroMarine Operations"
                className="w-full h-48 object-cover"
              />
            </div>
          </div>

          <div className="maritime-card p-8">
            <h2 className="text-2xl font-bold text-ocean-800 mb-6">Our Mission</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              To provide world-class offshore support services while maintaining the highest 
              standards of safety, environmental stewardship, and operational efficiency.
            </p>
            
            <h3 className="text-xl font-semibold text-ocean-700 mb-3">Core Values</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3"></span>
                Safety First - Zero tolerance for unsafe practices
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3"></span>
                Environmental Responsibility
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3"></span>
                Operational Excellence
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3"></span>
                Client Satisfaction
              </li>
            </ul>
          </div>
        </div>

        <div className="maritime-card p-8 mb-16">
          <h2 className="text-2xl font-bold text-ocean-800 mb-6 text-center">Our Expertise</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-ocean-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">Vessel Management</h3>
              <p className="text-gray-600 text-sm">Comprehensive fleet management services</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-ocean-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">HSE Compliance</h3>
              <p className="text-gray-600 text-sm">Health, Safety & Environmental standards</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-ocean-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">Crew Services</h3>
              <p className="text-gray-600 text-sm">Professional maritime crew recruitment</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-ocean-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">Project Support</h3>
              <p className="text-gray-600 text-sm">End-to-end offshore project assistance</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="maritime-card p-8">
            <h2 className="text-2xl font-bold text-ocean-800 mb-6">Our History</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-ocean-500 pl-6">
                <h3 className="font-semibold text-ocean-700">Founded</h3>
                <p className="text-gray-600">Established as a premier offshore support company</p>
              </div>
              <div className="border-l-4 border-ocean-400 pl-6">
                <h3 className="font-semibold text-ocean-700">Expansion</h3>
                <p className="text-gray-600">Extended operations across the Middle East region</p>
              </div>
              <div className="border-l-4 border-ocean-300 pl-6">
                <h3 className="font-semibold text-ocean-700">Present</h3>
                <p className="text-gray-600">Leading provider of comprehensive marine services</p>
              </div>
            </div>
          </div>

          <div className="maritime-card p-8">
            <h2 className="text-2xl font-bold text-ocean-800 mb-6">Certifications & Standards</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-center">
                <span className="w-3 h-3 bg-green-500 rounded-full mr-3"></span>
                ISO 9001:2015 Quality Management System
              </li>
              <li className="flex items-center">
                <span className="w-3 h-3 bg-green-500 rounded-full mr-3"></span>
                ISO 14001:2015 Environmental Management
              </li>
              <li className="flex items-center">
                <span className="w-3 h-3 bg-green-500 rounded-full mr-3"></span>
                ISO 45001:2018 Occupational Health & Safety
              </li>
              <li className="flex items-center">
                <span className="w-3 h-3 bg-green-500 rounded-full mr-3"></span>
                MLC 2006 Maritime Labour Convention
              </li>
              <li className="flex items-center">
                <span className="w-3 h-3 bg-green-500 rounded-full mr-3"></span>
                STCW International Standards
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;